/*******************************************************************/
/** Unit testable helper functions for streams/devices components **/
/*******************************************************************/

import { getStreams } from '@utils/graphql/creators/streams';
import { getDevices } from '@utils/graphql/creators/devices';
import { SSOT_ACTIONS } from '@utils/eventHandler';
import { getLogger } from '@utils/general';

const logger = getLogger('src.pages.split.streams.helpers');

/***************/
/** Constants **/
/***************/

const STREAM_MODEL = 'stream';
const DEVICE_MODEL = 'device';

const MODEL_FETCHERS = {
  [STREAM_MODEL]: getStreams,
  [DEVICE_MODEL]: getDevices,
};

const STREAM_LINKED_FIELDS = [
  'appliance_id',
  'zone_id',
  'source_id',
];


const LINKED_FIELDS = {
  [STREAM_MODEL]: STREAM_LINKED_FIELDS,
};

// @todo: Do we really need sets for all these? Why not just arrays?
const STREAM_NESTED_PATCH_FIELDS = {
  appliance: new Set([
    'id',
    'name',
    'management_address',
    'public_address',
  ]),
  zone: new Set(['id', 'name']),
  source: new Set([
    'id',
    'name',
    'device', // @todo: not grabbing nested field name, at least not yet
    'appliance', // not grabbing nested field name, at least not yet
  ]),
};

const DEVICE_NESTED_PATCH_FIELDS = {
  stream: new Set([
    'id',
    'name',
    'online',
  ]),
  source: new Set(['id', 'category']),
};

const NESTED_PATCH_FIELDS = {
  [STREAM_MODEL]: STREAM_NESTED_PATCH_FIELDS,
  [DEVICE_MODEL]: DEVICE_NESTED_PATCH_FIELDS,
};

const STREAM_PATCH_FIELDS = new Set([
  'id',
  'name',
  'description',
  'online',
  'clsps_address',
  'pull_on_request',
  'use_interleaved_rtsp_transport',
  'snapshot_interval',
  ...STREAM_LINKED_FIELDS,
]);

const DEVICE_PATCH_FIELDS = new Set([
  'id',
  'name',
  'description',
  'latitude',
  'longitude',
  'network_address',
  'metadata',
  'problem_camera',
  'type',
  'connection_type',
  'elevation',
  'username',
  'has_creds',
]);

const PATCH_FIELDS = {
  [STREAM_MODEL]: STREAM_PATCH_FIELDS,
  [DEVICE_MODEL]: DEVICE_PATCH_FIELDS,
};


/***********************/
/** PRIVATE FUNCTIONS **/
/***********************/


/**
 * Returns changed fields from `current` relative to `previous`,
 * filtered to only keys present in `patchFields`.
 *
 * @param {Set<string>} patchFields Set of field names allowed in the patch.
 * @param {object} current The current state from the event payload.
 * @param {object} previous The previous state from the event payload.
 * @returns {object} Key/value pairs of changed, patchable fields.
 */
const changedValues = (patchFields, current, previous) => {
  return Object.fromEntries(
    Object.entries(current ?? {}).filter(
      ([key, value]) => patchFields?.has(key) && value !== previous?.[key]
    )
  );
};

/**********************/
/** PUBLIC FUNCTIONS **/
/**********************/

/**
 * Removes duplicate rows by id, keeping the last occurrence of each.
 * Rows without an id are preserved using their array index as a fallback key.
 *
 * @param {object[]} [rows=[]] Array of table row objects.
 * @returns {object[]} Deduped array with unique ids.
 */
export const dedupeRowsById = (rows = []) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  const dedupedRowsById = new Map();
  rows.forEach((row, index) => {
    if (!row || row.id === undefined || row.id === null) {
      dedupedRowsById.set(`__index_${index}`, row);
      return;
    }
    dedupedRowsById.set(String(row.id), row);
  });

  return Array.from(dedupedRowsById.values());
};

/**
 * Parses a raw SSOT event message into a normalized structure.
 *
 * Returns null if the payload is missing, the action is empty,
 * the data can't be parsed as JSON, or current/previous are absent.
 *
 * @param {object} eventMessage Raw event from the message emitter.
 * @returns {{ model: string, actionType: string, current: object, previous: object }|null}
 */
export const parseEventMessage = (eventMessage) => {
  const payload = eventMessage?.detail?.payload;
  if (!payload) {
    return null;
  }

  const {
    model = '',
    action = '',
    data: rawData = {},
  } = payload;

  if (!action || !model || model === 'Apikey') {
    return null;
  }

  let data;
  try {
    data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
  } catch (error) {
    logger.error('Failed to parse event message.', error);
    return null;
  }

  if (data?.current === undefined || data?.previous === undefined) {
    return null;
  }

  return {
    model: model.toLowerCase(),
    actionType: action.charAt(0).toUpperCase() + action.slice(1).toLowerCase(),
    current: data.current,
    previous: data.previous,
  };
};

/**
 * Determines how to handle an SSOT event for a linked entity (e.g. a zone or
 * appliance update that affects stream rows or a stream/source update that affects
 * camera rows).
 *
 * @param {string} sourceModel The model whose table is being updated ('stream' or 'device').
 * @param {{ model: string, actionType: string, current: object, previous: object }} parsedEvent
 *   Parsed event from parseEventMessage.
 * @returns {{ valuesToUpdate: object|null, updatedId: string|null }}
 */
export const determineLinkedFieldEventAction = (
  sourceModel,
  {
    model,
    current,
    previous,
  }
) => {
  const nestedFields = NESTED_PATCH_FIELDS[sourceModel]?.[model];
  if (!nestedFields) {
    return { valuesToUpdate: {}, updatedId: null };
  }

  const valuesToUpdate = changedValues(nestedFields, current, previous);
  const updatedId = Object.keys(valuesToUpdate).length > 0 ? current.id : null;

  return { valuesToUpdate, updatedId };
};

/**
 * Determines what table action to take in response to a direct model SSOT event
 * (i.e. the event model matches the table's source model).
 *
 * - 'update': computes changed patchable fields; sets fetchNeeded if any linked id fields changed.
 * - 'remove': returns the id to remove from the table.
 * - 'create': returns the id to fetch and add to the table.
 *
 * @param {{ model: string, actionType: string, current: object, previous: object }} parsedEvent
 *   Parsed event from parseEventMessage.
 * @returns {{ fetchNeeded: boolean, valuesToUpdate: object|null, removedId: string|null,
 *   createdId: string|null, updatedId: string|null }}
 */
export const determineEventAction = ({
  model,
  actionType,
  current,
  previous,
}) => {
  let fetchNeeded = false;
  let valuesToUpdate = null;
  let removedId = null;
  let createdId = null;
  let updatedId = null;

  switch (actionType) {
    case SSOT_ACTIONS.UPDATE: {
      valuesToUpdate = changedValues(PATCH_FIELDS[model], current, previous);
      if (Object.keys(valuesToUpdate).length > 0) {
        updatedId = current.id;
        fetchNeeded = LINKED_FIELDS[model]?.some((field) => field in valuesToUpdate);
      }
      break;
    }
    case SSOT_ACTIONS.REMOVE:
      removedId = previous.id;
      break;
    case SSOT_ACTIONS.CREATE:
      createdId = current.id;
      fetchNeeded = true;
      break;
    default:
      break;
  }

  return {
    fetchNeeded,
    valuesToUpdate,
    removedId,
    createdId,
    updatedId,
  };
};

/**
 * Fetches a single model record by id from the API.
 *
 * Returns null if the model is unknown, the id is not a valid MongoDB ObjectId,
 * returnProperties is empty, or the request fails.
 *
 * @param {string} model Model type to fetch ('stream' or 'device').
 * @param {string} id MongoDB ObjectId of the record to fetch.
 * @param {string[]} returnProperties Fields to include in the response (e.g. 'zone.name').
 * @returns {Promise<object|null>} The fetched data object, or null on any failure.
 */
export const retrieveModelById = async (model, id, returnProperties) => {
  const fetcher = MODEL_FETCHERS[model];
  if (!fetcher) {
    logger.error(`model must be a valid, non-empty string, received "${model}"`);
    return null;
  }
  const MONGODB_OBJECT_ID_REGEX = /^[a-f\d]{24}$/i;
  if (typeof id !== 'string' || !MONGODB_OBJECT_ID_REGEX.test(id)) {
    logger.error(`id must be a MongoDB ObjectId string, received "${id}"`);
    return null;
  }
  if (!Array.isArray(returnProperties) || returnProperties.length === 0) {
    logger.error(`returnProperties must be a non-empty array, received "${returnProperties}"`);
    return null;
  }

  const { query, execute } = fetcher(id);
  query.addReturnProperties(...returnProperties);

  try {
    const { error, data } = await execute();
    if (error) {
      logger.error(error);
      return null;
    }
    return data;
  } catch (e) {
    logger.warn(e);
    return null;
  }
};

/**
 * Returns a buildFetchPatch function for use with handleTableEvent.
 *
 * For each field in `nestedFields`, the returned function produces:
 * - rowPatch: { [field]: fetched[field].name } — display string for the table cell
 * - actionPatch: { [field]: fetched[field] }   — full object so nested id lookups stay valid
 *
 * Only includes a field if it is present on the fetched object.
 *
 * @param {string[]} nestedFields Field names to extract (e.g. ['zone', 'appliance', 'source']).
 * @returns {(fetched: object) => { rowPatch: object, actionPatch: object }}
 */
export const buildNestedFetchPatch = (nestedFields) => (fetched) => {
  if (!Array.isArray(nestedFields)) return;

  const rowPatch = {};
  const actionPatch = {};

  for (const field of nestedFields) {
    if (fetched?.[field]?.name !== undefined) rowPatch[field] = fetched[field].name;
    if (fetched?.[field] !== undefined) actionPatch[field] = fetched[field];
  }
  return { rowPatch, actionPatch };
};
